import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  useDraggable,
  DragOverlay,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useDocument } from '@nandorojo/swr-firestore';
import { IinventoryItem } from '@typings/store/storeItem.interface';
import { IPhotoCard, IPhotoCardInventoryConfig, MAX_INVENTORY_SLOTS } from '@typings/photocard/photocard.interface';
import { updateInventorySlotIndex } from '@store/firebase/photocard/update-photocard';
import { ISoundEffect } from '@typings/setting/soundEffect.interface';
import { CollectionPath, SettingCollectionPath } from '@typings/collections.enum';
import { useAudioPlayer } from '@hooks/useAudioPlayer';
import useWindowWidth from '@hooks/useWindowWidth';
import InventorySlot from './InventorySlot';
import InventoryItem from './InventoryItem';
import InventoryDetailModal from './InventoryDetailModal';
import DragDropInfo from '@template/Info/DragDropInfo';

// ─── Styled ──────────────────────────────────────────────────────────────────

/** 게임 인벤토리 외곽 프레임 */
const BagFrame = styled.div`
  width: 100%;
  padding: 4px;
  background: var(--cardBackgroundColor, var(--bgColor));
  border: 1px solid var(--lineColor);
  border-radius: 6px;
  box-sizing: border-box;
`;

const GridContainer = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, 1fr);
  gap: 0px;
  width: 100%;
`;

const UnplacedSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const UnplacedTitle = styled.div`
  font-size: 11px;
  color: var(--subTextColor);
  font-weight: 600;
  padding: 4px 0 2px;
`;

const UnplacedGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, 1fr);
  gap: 8px;
  width: 100%;
`;

const EmptyMessage = styled.div`
  width: 100%;
  text-align: center;
  color: var(--subTextColor);
  padding: 20px 0;
  font-size: 13px;
`;

const DraggableItemWrapper = styled.div<{ $isDragging?: boolean }>`
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  touch-action: none;
  cursor: var(--hoverImgURL), pointer;
  border-radius: 4px;
  transition: box-shadow 0.15s, opacity 0.15s;

  &:active { cursor: var(--hoverImgURL), pointer; }

  & img {
    -webkit-user-drag: none;
    pointer-events: none;
  }

  ${({ $isDragging }) =>
    $isDragging &&
    `
    opacity: 0.3;
  `}
`;

// ─── 빈 칸 클릭 모달 ─────────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBox = styled.div`
  background: var(--bgColor);
  border-radius: 12px;
  padding: 20px;
  width: 280px;
  max-height: 60vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModalTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--textColor);
`;

const ModalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
`;

const ModalCancelBtn = styled.button`
  width: 100%;
  padding: 8px;
  border: 1px solid var(--lineColor);
  border-radius: 8px;
  background: transparent;
  color: var(--subTextColor);
  font-size: 13px;
  cursor: pointer;
`;

// ─── Props ───────────────────────────────────────────────────────────────────

type Props = {
  items: IinventoryItem[];
  config: IPhotoCardInventoryConfig;
  inventoryCollectionPath: string;
  isTabEdit?: boolean;
  photocardData?: IPhotoCard;
  visibleItemIds?: Set<string>;
};

// ─── Component ───────────────────────────────────────────────────────────────

function InventoryGridView({
  items,
  config,
  inventoryCollectionPath,
  isTabEdit,
  photocardData,
  visibleItemIds,
}: Props) {
  const { gridRows, expandedRows = 0 } = config;

  // 메인 그리드 유동 열 수 (아이템 50px, gap 0px 기준)
  const GRID_ITEM_SIZE = 50;
  const bagFrameRef = useRef<HTMLDivElement>(null);
  const [gridColumns, setGridColumns] = useState(4);

  const gridRafRef = useRef<number>(0);

  const measureGridColumns = useCallback(() => {
    if (!bagFrameRef.current) return;
    const width = bagFrameRef.current.clientWidth - 8;
    const computed = Math.max(1, Math.floor(width / GRID_ITEM_SIZE));
    setGridColumns(computed);
  }, []);

  const updateGridColumns = useCallback(() => {
    cancelAnimationFrame(gridRafRef.current);
    gridRafRef.current = requestAnimationFrame(measureGridColumns);
  }, [measureGridColumns]);

  // 초기 측정: paint 전에 동기적으로 실행하여 4단 깜빡임 방지
  useLayoutEffect(() => {
    measureGridColumns();
  }, [measureGridColumns]);

  useEffect(() => {
    const observer = new ResizeObserver(updateGridColumns);
    if (bagFrameRef.current) observer.observe(bagFrameRef.current);
    return () => {
      cancelAnimationFrame(gridRafRef.current);
      observer.disconnect();
    };
  }, [updateGridColumns]);

  // 유효 행 수 = 관리자 기본 행 + 확장권 행, 최대 200칸 상한 적용
  const effectiveRows = Math.min(
    gridRows + expandedRows,
    Math.floor(MAX_INVENTORY_SLOTS / Math.max(gridColumns, 1)),
  );
  const totalSlots = gridColumns * effectiveRows;

  // ─── 사운드 ──────────────────────────────────────────────────────────────────
  const { data: soundEffect } = useDocument<ISoundEffect>(
    SettingCollectionPath.SOUNDEFFECT,
    { listen: false },
  );
  const { play } = useAudioPlayer(
    {
      card: soundEffect?.cardSound?.url,
      list: soundEffect?.listSound?.url || soundEffect?.buttonSound?.url,
      menu: soundEffect?.listSound?.url,
    },
    Number(soundEffect?.volume) || 0.3,
  );

  // isPendingRef: listen:true + batch update 레이스 컨디션 방지
  const isPendingRef = useRef(false);
  const [localItems, setLocalItems] = useState<IinventoryItem[]>(items);

  useEffect(() => {
    if (!isPendingRef.current) {
      setLocalItems(items);
    }
  }, [items]);

  // slotIndex → 아이템 맵 (범위 내 배치된 아이템만)
  const slotMap = React.useMemo(() => {
    const map = new Map<number, IinventoryItem>();
    localItems.forEach((item) => {
      const idx = item.slotIndex;
      if (idx !== undefined && idx !== null && idx < totalSlots) {
        map.set(idx, item);
      }
    });
    return map;
  }, [localItems, totalSlots]);

  // 미배치 아이템: slotIndex 없거나 그리드 범위 초과
  const unplacedItems = React.useMemo(
    () =>
      localItems.filter((item) => {
        const idx = item.slotIndex;
        return idx === undefined || idx === null || idx >= totalSlots;
      }),
    [localItems, totalSlots],
  );

  // 미정리 아이템 그리드 유동 열 수
  const UNPLACED_ITEM_SIZE = 50;
  const UNPLACED_ITEM_GAP = 8;
  const unplacedGridRef = useRef<HTMLDivElement>(null);
  const [unplacedColumns, setUnplacedColumns] = useState(4);

  const unplacedRafRef = useRef<number>(0);

  const measureUnplacedColumns = useCallback(() => {
    if (!unplacedGridRef.current) return;
    const width = unplacedGridRef.current.clientWidth;
    const computed = Math.max(1, Math.floor((width + UNPLACED_ITEM_GAP) / (UNPLACED_ITEM_SIZE + UNPLACED_ITEM_GAP)));
    setUnplacedColumns(computed);
  }, []);

  const updateUnplacedColumns = useCallback(() => {
    cancelAnimationFrame(unplacedRafRef.current);
    unplacedRafRef.current = requestAnimationFrame(measureUnplacedColumns);
  }, [measureUnplacedColumns]);

  // 초기 측정: paint 전에 동기적으로 실행하여 4단 깜빡임 방지
  useLayoutEffect(() => {
    measureUnplacedColumns();
  }, [measureUnplacedColumns]);

  useEffect(() => {
    const observer = new ResizeObserver(updateUnplacedColumns);
    if (unplacedGridRef.current) observer.observe(unplacedGridRef.current);
    return () => {
      cancelAnimationFrame(unplacedRafRef.current);
      observer.disconnect();
    };
  }, [updateUnplacedColumns]);

  // PC / 모바일 분기 (480px)
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth <= 480;

  // 빈 칸 클릭 → 아이템 선택 모달 (모바일 전용)
  const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | null>(null);

  // 드래그 중인 아이템 (DragOverlay용)
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  // hover 소리 중복 방지: 마지막으로 소리 낸 슬롯 ID 추적
  const lastHoveredSlotRef = useRef<string | null>(null);
  const activeItem = activeItemId
    ? localItems.find((item) => item.id === activeItemId)
    : null;

  // dnd-kit v4는 collision detection에 pointerCoordinates를 전달하지 않으므로
  // 마우스/터치 위치를 직접 추적하여 실시간 좌표 기반 충돌 감지에 활용
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => {
    const onMouse = (e: MouseEvent) => {
      pointerRef.current = { x: e.clientX, y: e.clientY };
    };
    const onTouch = (e: TouchEvent) => {
      if (e.touches[0]) {
        pointerRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };
    window.addEventListener('mousemove', onMouse);
    window.addEventListener('touchmove', onTouch, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMouse);
      window.removeEventListener('touchmove', onTouch);
    };
  }, []);

  // 실시간 getBoundingClientRect 기반 충돌 감지 (overflow:hidden / scroll 환경에서도 정확)
  const slotOnlyCollision = useCallback((args: any) => {
    const { droppableContainers } = args;
    const pointer = pointerRef.current;

    if (pointer) {
      for (const container of droppableContainers) {
        const id = String(container.id);
        if (!id.startsWith('slot-')) continue;
        const node = container.node?.current;
        if (!node) continue;
        const rect = node.getBoundingClientRect();
        if (
          pointer.x >= rect.left &&
          pointer.x <= rect.right &&
          pointer.y >= rect.top &&
          pointer.y <= rect.bottom
        ) {
          return container.id;
        }
      }
    }

    // 포인터가 슬롯 위에 없으면 closestCenter 폴백
    const filtered = {
      ...args,
      droppableContainers: droppableContainers.filter(
        (c: any) => String(c.id).startsWith('slot-'),
      ),
    };
    return closestCenter(filtered);
  }, []);

  // DnD sensors: distance 기반으로 즉시 드래그 시작 (5px 이동 시)
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 5 } }),
  );

  function handleDragStart(event: DragStartEvent) {
    setActiveItemId(String(event.active.id));
    lastHoveredSlotRef.current = null;
    if (soundEffect?.isActive) play('list');
  }

  function handleDragOver(event: DragOverEvent) {
    const overId = event.over ? String(event.over.id) : null;
    if (!overId || !overId.startsWith('slot-')) return;
    if (overId === lastHoveredSlotRef.current) return;
    lastHoveredSlotRef.current = overId;
    if (soundEffect?.isActive) play('card');
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveItemId(null);
    if (!over) return;

    const activeItem = localItems.find((item) => item.id === active.id);
    if (!activeItem) return;

    const overIdStr = String(over.id);
    if (!overIdStr.startsWith('slot-')) return;
    const targetSlotIndex = parseInt(overIdStr.replace('slot-', ''), 10);
    if (isNaN(targetSlotIndex)) return;
    if (targetSlotIndex === activeItem.slotIndex) return;

    if (soundEffect?.isActive) play('menu');

    const occupant = slotMap.get(targetSlotIndex);
    const isUnplaced =
      activeItem.slotIndex === undefined ||
      activeItem.slotIndex === null ||
      activeItem.slotIndex >= totalSlots;

    const newItems = localItems.map((item) => {
      if (item.id === activeItem.id) {
        return { ...item, slotIndex: targetSlotIndex };
      }
      if (occupant && item.id === occupant.id) {
        // swap: 미배치 아이템이면 기존 점유 아이템을 미배치로
        return { ...item, slotIndex: isUnplaced ? undefined : activeItem.slotIndex };
      }
      return item;
    });

    setLocalItems(newItems);
    isPendingRef.current = true;

    const changedItems: { id: string; slotIndex: number | undefined }[] = [
      { id: activeItem.id!, slotIndex: targetSlotIndex },
    ];
    if (occupant) {
      changedItems.push({
        id: occupant.id!,
        slotIndex: isUnplaced ? undefined : activeItem.slotIndex,
      });
    }

    updateInventorySlotIndex({
      collectionPath: inventoryCollectionPath,
      items: changedItems,
    })
      .then((promises) => Promise.all(promises as Promise<any>[]))
      .finally(() => {
        isPendingRef.current = false;
      });
  }

  /** 우클릭(PC) / 더블탭(모바일): 슬롯에서 장착 해제 → 미정리로 이동 */
  function handleUnequip(item: IinventoryItem) {
    if (!isTabEdit || !item.id) return;

    if (soundEffect?.isActive) play('card');

    const newItems = localItems.map((i) =>
      i.id === item.id ? { ...i, slotIndex: undefined } : i,
    );
    setLocalItems(newItems);
    isPendingRef.current = true;

    updateInventorySlotIndex({
      collectionPath: inventoryCollectionPath,
      items: [{ id: item.id, slotIndex: undefined }],
    })
      .then((promises) => Promise.all(promises as Promise<any>[]))
      .finally(() => {
        isPendingRef.current = false;
      });
  }

  function handleEmptySlotClick(slotIndex: number) {
    if (!isTabEdit) return;
    setSelectedSlotIndex(slotIndex);
  }

  function handleSelectUnplaced(item: IinventoryItem) {
    if (selectedSlotIndex === null) return;

    if (soundEffect?.isActive) play('menu');
    const occupant = slotMap.get(selectedSlotIndex);

    const newItems = localItems.map((i) => {
      if (i.id === item.id) return { ...i, slotIndex: selectedSlotIndex };
      if (occupant && i.id === occupant.id) return { ...i, slotIndex: undefined };
      return i;
    });

    setLocalItems(newItems);
    setSelectedSlotIndex(null);
    isPendingRef.current = true;

    const changedItems = [
      { id: item.id, slotIndex: selectedSlotIndex },
      ...(occupant ? [{ id: occupant.id, slotIndex: undefined }] : []),
    ];

    updateInventorySlotIndex({
      collectionPath: inventoryCollectionPath,
      items: changedItems,
    })
      .then((promises) => Promise.all(promises as Promise<any>[]))
      .finally(() => {
        isPendingRef.current = false;
      });
  }

  return (
    <>
      {isTabEdit && <DragDropInfo variant="grid" />}
      <DndContext
        sensors={sensors}
        collisionDetection={slotOnlyCollision as any}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <BagFrame ref={bagFrameRef}>
          <GridContainer $columns={gridColumns}>
            {Array.from({ length: totalSlots }, (_, idx) => {
              const item = slotMap.get(idx);
              const isHidden = item && visibleItemIds
                ? !visibleItemIds.has(item.id || '')
                : false;
              return (
                <InventorySlot
                  key={idx}
                  slotIndex={idx}
                  item={item}
                  isDraggable={isTabEdit}
                  onEmptySlotClick={handleEmptySlotClick}
                  onUnequip={handleUnequip}
                  onSlotHover={() => { if (soundEffect?.isActive) play('card'); }}
                  photocardData={photocardData}
                  hidden={isHidden}
                />
              );
            })}
          </GridContainer>
        </BagFrame>

        {/* 미배치 아이템 영역 */}
        {unplacedItems.length > 0 && (
          <UnplacedSection>
            <UnplacedTitle>미정리 아이템 ({unplacedItems.length})</UnplacedTitle>
            <UnplacedGrid ref={unplacedGridRef} $columns={unplacedColumns}>
              {unplacedItems.map((item, i) => {
                const isHidden = visibleItemIds
                  ? !visibleItemIds.has(item.id || '')
                  : false;
                return isTabEdit && !isMobile ? (
                  <DraggableUnplacedItem
                    key={`${item.id}-${i}`}
                    item={item}
                    photocardData={photocardData}
                    isHidden={isHidden}
                  />
                ) : (
                  <div
                    key={`${item.id}-${i}`}
                    style={{
                      opacity: isHidden ? 0.15 : 1,
                      pointerEvents: isHidden ? 'none' : 'auto',
                      transition: 'opacity 0.2s',
                    }}
                  >
                    <InventoryItem
                      data={item}
                      photocardData={photocardData}
                      modal={<InventoryDetailModal />}
                      collectionPath={CollectionPath.STORE}
                      followTooltip={false}
                    />
                  </div>
                );
              })}
            </UnplacedGrid>
          </UnplacedSection>
        )}

        {/* DragOverlay: Portal로 body에 렌더 — overflow:hidden / transform 부모에 잘리지 않도록 */}
        {ReactDOM.createPortal(
          <DragOverlay
            dropAnimation={{ duration: 200, easing: 'ease' }}
            style={{ zIndex: 999 }}
          >
            {activeItem ? (
              <div style={{
                width: 50,
                height: 50,
                opacity: 0.9,
                boxShadow: '0 6px 20px rgba(0,0,0,0.35), 0 0 8px rgba(99,102,241,0.4)',
                borderRadius: 4,
              }}>
                <InventoryItem
                  data={activeItem}
                  photocardData={photocardData}
                  collectionPath={CollectionPath.STORE}
                  followTooltip={false}
                />
              </div>
            ) : null}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>

      {localItems.length === 0 && (
        <EmptyMessage>인벤토리가 비어있습니다.</EmptyMessage>
      )}

      {/* 빈 칸 클릭 → 미배치 아이템 선택 모달 */}
      {selectedSlotIndex !== null && (
        <ModalOverlay onClick={() => setSelectedSlotIndex(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTitle>슬롯 {selectedSlotIndex + 1}에 배치할 아이템 선택</ModalTitle>
            {unplacedItems.length === 0 ? (
              <EmptyMessage>미정리 아이템이 없습니다.</EmptyMessage>
            ) : (
              <ModalGrid>
                {unplacedItems.map((item, i) => (
                  <div
                    key={`modal-${item.id}-${i}`}
                    onClick={() => handleSelectUnplaced(item)}
                  >
                    <InventoryItem
                      data={item}
                      photocardData={photocardData}
                      collectionPath={CollectionPath.STORE}
                      followTooltip={false}
                    />
                  </div>
                ))}
              </ModalGrid>
            )}
            <ModalCancelBtn onClick={() => setSelectedSlotIndex(null)}>
              취소
            </ModalCancelBtn>
          </ModalBox>
        </ModalOverlay>
      )}
    </>
  );
}

// ─── PC 전용: 드래그 가능한 미정리 아이템 ─────────────────────────────────────

function DraggableUnplacedItem({
  item,
  photocardData,
  isHidden,
}: {
  item: IinventoryItem;
  photocardData?: IPhotoCard;
  isHidden: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: item.id || '' });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isHidden ? 0.15 : 1,
    pointerEvents: isHidden ? 'none' : 'auto',
    transition: 'opacity 0.2s',
  };

  return (
    <DraggableItemWrapper
      ref={setNodeRef}
      style={style}
      $isDragging={isDragging}
      {...listeners}
      {...attributes}
    >
      <InventoryItem
        data={item}
        photocardData={photocardData}
        modal={<InventoryDetailModal />}
        collectionPath={CollectionPath.STORE}
        followTooltip={false}
      />
    </DraggableItemWrapper>
  );
}

export default InventoryGridView;
